import { useWeb3React } from '@web3-react/core'
import copy from 'copy-to-clipboard'
import { memo, useCallback, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'store'
import { useModal } from 'store/app/hooks'
import { FlexCenter } from 'styles'
import { encryptionAddress } from 'utils/address'
import { viewOnZKLinkExplorer } from 'utils/explorer'
import { updateModal } from '../../../store/app/actions/modalActions'
import { useDisconnect } from '../../../hooks/web3/useDisconnect'
import { AssetsEyes } from '../../../components/AssetsEyes'
import CopyIcon from './CopyIcon'
import QuitIcon from './QuitIcon'
import ViewIcon from './ViewIcon'
import { ReactComponent as SvgPinIcon } from './pin.svg'
import { ReactComponent as SvgUnPinIcon } from './unpin.svg'
import { HistoryTabKeys } from 'views/History/Tab'
import { useNavigate } from 'react-router-dom'
import { updateHistoryTabAction } from 'store/history/actions'
import { ReactComponent as SvgIconRightArrow } from './rightArrow.svg'
import { ReactComponent as SvgProfit } from './profit.svg'
import toastify from 'components/Toastify'
import { useEffect } from 'react'
import { updateFavoriteAssetsElements } from 'store/settings/actions'
import { useFavoriteAssetsElements } from 'store/settings/hooks'
import { useAccountAssetList } from './hooks'
import { ZKTab } from 'components/ZKTab'

import {
  Popup,
  AccountHeader,
  StyledSvgELipse,
  StyledAddress,
  StyledBody,
  BtnWrap,
  DepositButton,
  AccountOperate,
  IconRect,
  IconRectWrap,
  TabWrap,
  AssetsItem,
  AssetsItemTitle,
  ItemValue,
  AssetsBorderLine,
  ContentWrap,
  PortfolioWrap,
  // HelpBoxWrap,
  // QAHelpTitle,
  // QAHelpDescBox,
  HistoryTab,
} from './styles'
import { MaskedAssets } from 'components/MaskedAssets'
import { useSetAtom } from 'jotai'
import { navIndicatorAtom } from '../NavIndicator/atoms'
import { MobileHead } from '../MobileHead'
import { menuAtom } from '../Menu/atoms'
import { isMobile } from 'utils/userAgent'

export enum TABS {
  HISTORY = 'history',
  ASSETS = 'assets',
}

export const AccountModal = memo(() => {
  const dispatch = useAppDispatch()
  const accountModal = useModal('account')
  const { account = '', ENSName } = useWeb3React()
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(TABS.ASSETS)
  // const MAX_PIN = 3

  const navigate = useNavigate()
  const signOut = useDisconnect()
  const favoriteAssetsElements = useFavoriteAssetsElements()
  const { memoAssetsList } = useAccountAssetList()
  const [showTipText, setShowTipText] = useState({
    index: -1,
    text: '',
  })
  // const [showMaxTip, setShowMaxTip] = useState(false)

  useEffect(() => {
    if (accountModal) {
      setActiveTab(TABS.ASSETS)
    }
  }, [accountModal])

  // useEffect(() => {
  //   if (favoriteAssetsElements.length === MAX_PIN) {
  //     setShowMaxTip(true)
  //     setTimeout(() => {
  //       setShowMaxTip(false)
  //     }, 3000)
  //   }
  // }, [favoriteAssetsElements])

  const memoTabs = useMemo(() => {
    const l = [
      {
        value: TABS.ASSETS,
        label: t('Assets'),
      },
    ]
    if (!isMobile) {
      l.push({
        value: TABS.HISTORY,
        label: t('History'),
      })
    }
    return l
  }, [t, isMobile])

  const copyAddress = useCallback(() => {
    if (ENSName || account) {
      copy(ENSName ?? account)
      toastify.success(t('account-copied', { defaultValue: 'Copied' }))
    }
  }, [ENSName, account, t])

  const renderAssetsList = () => {
    return (
      <>
        {memoAssetsList &&
          memoAssetsList.length > 0 &&
          memoAssetsList.map((item, i) => {
            return (
              <div key={item.type}>
                <AssetsItem>
                  <AssetsItemTitle>
                    {favoriteAssetsElements.indexOf(item.type) > -1 && (
                      <SvgPinIcon
                        onClick={() => {
                          if (favoriteAssetsElements.length >= 0) {
                            dispatch(
                              updateFavoriteAssetsElements({
                                type: 'del',
                                value: item.type,
                              }),
                            )
                          }
                        }}
                      />
                    )}
                    {favoriteAssetsElements.indexOf(item.type) < 0 && (
                      <SvgUnPinIcon
                        onClick={() => {
                          if (favoriteAssetsElements.length !== 3) {
                            dispatch(
                              updateFavoriteAssetsElements({
                                type: 'add',
                                value: item.type,
                              }),
                            )
                          }
                        }}
                      />
                    )}

                    {item.title}
                  </AssetsItemTitle>
                  <ItemValue>
                    <MaskedAssets>
                      {item?.render ? item?.render() : item?.value}
                    </MaskedAssets>
                  </ItemValue>
                </AssetsItem>

                {i === 0 && <AssetsBorderLine />}
              </div>
            )
          })}
        {/* {favoriteAssetsElements.length === MAX_PIN && showMaxTip && (
          <PinupTexrt>{t('Pin Up to 3 elments')}</PinupTexrt>
        )} */}
      </>
    )
  }

  const renderHistoryList = () => {
    return (
      <>
        {HistoryTabKeys.slice(1).map((item, index) => {
          return (
            <HistoryTab
              onClick={() => {
                dispatch(updateModal({ modal: 'account', open: false }))
                dispatch(updateHistoryTabAction({ tab: index }))
                navigate(`/portfolio/history/${item}`)
              }}
              key={item}
            >
              {item}
              <SvgIconRightArrow />
            </HistoryTab>
          )
        })}
      </>
    )
  }

  const IconElements = [<CopyIcon />, <ViewIcon />, <QuitIcon />]

  const setNavIndicator = useSetAtom(navIndicatorAtom)
  const setMenu = useSetAtom(menuAtom)

  return (
    <Popup>
      <MobileHead onClose={() => setMenu(0)} />
      <AccountHeader>
        <FlexCenter>
          <StyledSvgELipse />
          <StyledAddress>{ENSName ?? encryptionAddress(account)}</StyledAddress>
          <AssetsEyes />
        </FlexCenter>
        <AccountOperate>
          {IconElements.map((d, i) => {
            return (
              <IconRectWrap key={i}>
                {showTipText.index === i && (
                  <span
                    className="tip"
                    style={{ left: i === 2 ? '-10px' : i === 1 ? '-3px' : '' }}
                  >
                    {showTipText?.text}
                  </span>
                )}
                <IconRect
                  onMouseEnter={() => {
                    setShowTipText({
                      index: i,
                      text:
                        i === 0 ? 'Copy' : i === 1 ? 'Explore' : 'Disconnect',
                    })
                  }}
                  onMouseLeave={() => {
                    setShowTipText({
                      index: -1,
                      text: '',
                    })
                  }}
                  onClick={() => {
                    if (i === 0) {
                      copyAddress()
                    } else if (i === 1) {
                      viewOnZKLinkExplorer(account)
                    } else {
                      signOut()
                    }

                    setNavIndicator((prev) => ({
                      ...prev,
                      width: 0,
                    }))
                  }}
                >
                  {d}
                </IconRect>
              </IconRectWrap>
            )
          })}
        </AccountOperate>
      </AccountHeader>
      <StyledBody>
        <BtnWrap direction="row" spacing={1}>
          <DepositButton
            onClick={() => {
              dispatch(updateModal({ modal: 'account', open: false }))
              dispatch(updateModal({ modal: 'deposit', open: true }))
            }}
          >
            Deposit
          </DepositButton>
          <DepositButton
            onClick={() => {
              dispatch(updateModal({ modal: 'account', open: false }))
              dispatch(updateModal({ modal: 'withdraw', open: true }))
            }}
          >
            Withdraw
          </DepositButton>
        </BtnWrap>
        <TabWrap>
          <ZKTab
            tabs={memoTabs}
            tabClick={(row) => {
              setActiveTab(row.value)
            }}
            wrapStyle={{ gap: '18px' }}
            activeValue={activeTab}
            itemStyle={{ paddingBottom: '5px' }}
          />
        </TabWrap>
        <ContentWrap>
          {activeTab === 'assets' && renderAssetsList()}
          {activeTab === 'history' && renderHistoryList()}
          {isMobile ? null : (
            <PortfolioWrap onClick={() => navigate('/portfolio/assets')}>
              <div className="left">
                <p className="title">Portfolio</p>
                <p className="desc">
                  View and manage all your assets on the exchange.
                </p>
              </div>
              <SvgProfit />
            </PortfolioWrap>
          )}
        </ContentWrap>
      </StyledBody>
    </Popup>
  )
})
